import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Star, Plus, Pencil, Trash2, PackageX, X } from "lucide-react";
import { Toggle } from "@/pages/admin/AdminProducts";
import {
  Field, TextInput, TextArea, SingleImageUploader, GalleryUploader, Modal, slugPreview,
} from "@/pages/admin/adminShared";

const EMPTY_ITEM = { name: "", weight: "" };
const EMPTY_FORM = {
  slug: null, name: "", slugField: "", tagline: "", serves: "", badge: "",
  price: "", original_price: "", description: "", featured: false, active: true,
  image: "", images: [], items: [{ ...EMPTY_ITEM }],
};

export default function AdminPackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/packages").then((r) => {
      setPackages(r.data);
      setLoading(false);
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = async (slug, patch) => {
    try {
      await api.patch(`/admin/packages/${slug}`, patch);
      setPackages((ps) => ps.map((p) => (p.slug === slug ? { ...p, ...patch } : p)));
    } catch (e) {
      alert("Error al actualizar");
    }
  };

  const openCreate = () => { setErr(""); setForm({ ...EMPTY_FORM, items: [{ ...EMPTY_ITEM }] }); };
  const openEdit = (p) => {
    setErr("");
    setForm({
      slug: p.slug, name: p.name || "", slugField: p.slug || "", tagline: p.tagline || "",
      serves: p.serves || "", badge: p.badge || "", price: p.price ?? "",
      original_price: p.original_price ?? "", description: p.description || "",
      featured: !!p.featured, active: p.active !== false,
      image: p.image || "", images: p.images || [],
      items: (p.items && p.items.length ? p.items : [{ ...EMPTY_ITEM }]).map((it) => ({ name: it.name || "", weight: it.weight || "" })),
    });
  };
  const closeForm = () => setForm(null);

  const setItem = (idx, key, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [key]: value };
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { ...EMPTY_ITEM }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) { setErr("El nombre es obligatorio"); return; }
    if (!form.price || parseFloat(form.price) <= 0) { setErr("El precio debe ser mayor a 0"); return; }
    const items = form.items.filter((it) => it.name.trim());
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      slug: form.slugField.trim() || undefined,
      tagline: form.tagline.trim() || undefined,
      serves: form.serves.trim() || undefined,
      badge: form.badge.trim() || undefined,
      price: parseFloat(form.price),
      original_price: form.original_price ? parseFloat(form.original_price) : undefined,
      description: form.description.trim(),
      featured: form.featured,
      active: form.active,
      image: form.image || undefined,
      images: form.images,
      items,
    };
    try {
      if (form.slug) {
        await api.patch(`/admin/packages/${form.slug}`, payload);
      } else {
        await api.post("/admin/packages", payload);
      }
      closeForm();
      load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Error al guardar el paquete");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/packages/${p.slug}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div className="p-8 md:p-12" data-testid="admin-packages">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-2">⟡ Admin</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-wider uppercase text-white leading-none">Paquetes</h1>
        </div>
        <button
          onClick={openCreate}
          className="btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-sm tracking-widest uppercase px-5 py-3 inline-flex items-center gap-2"
          data-testid="new-package-btn"
        >
          <Plus className="w-4 h-4" /> Nuevo paquete
        </button>
      </div>

      <div className="bg-arre-surface border border-arre-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-arre-border text-[10px] tracking-[0.2em] uppercase text-white/50">
              <th className="text-left p-4">Paquete</th>
              <th className="text-left p-4">Sirve</th>
              <th className="text-left p-4">Items</th>
              <th className="text-left p-4">Precio</th>
              <th className="text-left p-4">Destacado</th>
              <th className="text-left p-4">Activo</th>
              <th className="text-left p-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="p-8 text-center text-white/50">Cargando...</td></tr>}
            {!loading && packages.length === 0 && (
              <tr><td colSpan="7" className="p-10 text-center">
                <PackageX className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <div className="text-white/50">Sin paquetes todavía. Crea el primero, compare.</div>
              </td></tr>
            )}
            {packages.map((p) => (
              <tr key={p.slug} className="border-b border-arre-border/50" data-testid={`admin-package-${p.slug}`}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <img src={p.image} alt={p.name} className="w-12 h-12 object-cover border border-arre-border" />
                    <div>
                      <div className="font-display text-white">{p.name}</div>
                      <div className="text-xs text-white/40 italic">{p.tagline}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-white/70">{p.serves}</td>
                <td className="p-4 text-white/70">{p.items?.length || 0}</td>
                <td className="p-4">
                  <div className="flex items-center gap-1 text-white font-display">
                    <span className="text-white/50 text-sm">$</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      defaultValue={p.price}
                      onBlur={(e) => {
                        const nv = parseFloat(e.target.value);
                        if (!isNaN(nv) && nv > 0 && nv !== p.price) update(p.slug, { price: nv });
                      }}
                      className="w-28 bg-arre-black border border-arre-border px-2 py-1 text-white focus:outline-none focus:border-arre-red"
                      data-testid={`pkg-price-${p.slug}`}
                    />
                  </div>
                </td>
                <td className="p-4">
                  <button
                    onClick={() => update(p.slug, { featured: !p.featured })}
                    className={`${p.featured ? "text-arre-red" : "text-white/30"} hover:scale-110 transition`}
                  >
                    <Star className="w-5 h-5" fill={p.featured ? "currentColor" : "none"} />
                  </button>
                </td>
                <td className="p-4">
                  <Toggle active={p.active !== false} onChange={(v) => update(p.slug, { active: v })} testid={`pkg-active-${p.slug}`} />
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <button onClick={() => openEdit(p)} className="text-white/50 hover:text-white" title="Editar" data-testid={`edit-package-${p.slug}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(p)} className="text-white/40 hover:text-arre-red" title="Eliminar" data-testid={`delete-package-${p.slug}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <Modal title={form.slug ? "Editar paquete" : "Nuevo paquete"} onClose={closeForm} wide>
          <form onSubmit={save} className="space-y-4" data-testid="package-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nombre">
                <TextInput
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value, slugField: form.slug ? form.slugField : slugPreview(e.target.value) })}
                  placeholder="Arre Plebes"
                  data-testid="package-name-input"
                />
              </Field>
              <Field label="Slug (URL)">
                <TextInput value={form.slugField} onChange={(e) => setForm({ ...form, slugField: e.target.value })} placeholder="arre-plebes" />
              </Field>
              <Field label="Tagline">
                <TextInput value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Para empezar fuerte, compare" />
              </Field>
              <Field label="Sirve a (ej. '2-3 personas')">
                <TextInput value={form.serves} onChange={(e) => setForm({ ...form, serves: e.target.value })} placeholder="2-3 personas" />
              </Field>
              <Field label="Precio ($ MXN)">
                <TextInput
                  required type="number" min="0" step="1"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  data-testid="package-price-input"
                />
              </Field>
              <Field label="Precio original (opcional, para mostrar descuento)">
                <TextInput type="number" min="0" step="1" value={form.original_price} onChange={(e) => setForm({ ...form, original_price: e.target.value })} />
              </Field>
              <Field label="Badge (ej. 'Popular', opcional)">
                <TextInput value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} placeholder="Popular" />
              </Field>
              <div className="flex items-center gap-6 pt-6">
                <label className="flex items-center gap-2 text-white/70 text-sm">
                  <Toggle active={form.featured} onChange={(v) => setForm({ ...form, featured: v })} /> Destacado
                </label>
                <label className="flex items-center gap-2 text-white/70 text-sm">
                  <Toggle active={form.active} onChange={(v) => setForm({ ...form, active: v })} /> Activo
                </label>
              </div>
            </div>

            <Field label="Descripción">
              <TextArea
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="El paquete para arrancar. Mezcla equilibrada de cortes..."
              />
            </Field>

            <Field label="Contenido del paquete">
              <div className="space-y-2">
                {form.items.map((it, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <TextInput
                      placeholder="Chorizo Argentino"
                      value={it.name}
                      onChange={(e) => setItem(idx, "name", e.target.value)}
                      className="flex-1"
                    />
                    <TextInput
                      placeholder="500 g"
                      value={it.weight}
                      onChange={(e) => setItem(idx, "weight", e.target.value)}
                      className="w-28"
                    />
                    <button type="button" onClick={() => removeItem(idx)} className="text-white/40 hover:text-arre-red">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addItem}
                  className="text-[10px] tracking-widest uppercase border border-arre-border px-3 py-1.5 text-white/70 hover:text-white hover:border-white inline-flex items-center gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" /> Agregar item
                </button>
              </div>
            </Field>

            <SingleImageUploader
              label="Imagen de portada"
              value={form.image}
              onChange={(url) => setForm({ ...form, image: url })}
            />

            <GalleryUploader
              label="Galería / carrusel (imágenes adicionales del paquete)"
              values={form.images}
              onChange={(imgs) => setForm({ ...form, images: imgs })}
            />

            {err && <div className="text-arre-red text-sm">{err}</div>}

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={closeForm} className="px-5 py-3 text-white/60 hover:text-white text-sm tracking-widest uppercase">
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-sm tracking-widest uppercase px-6 py-3 disabled:opacity-50"
                data-testid="save-package-btn"
              >
                {saving ? "Guardando..." : form.slug ? "Guardar cambios" : "Crear paquete"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
